import { useMemo } from 'react';
import { GTM_CLICK_CONTACT_US_BUTTON_JOIN_EXP } from 'constants/events';
import { useIsMobile } from './use-size-class';

type MenuItem = {
  id: string;
  label: string;
  link?: string;
  newTab?: boolean;
  mobileOnly?: boolean;
  onClick?: () => void;
};

type ButtonLabels = {
  contactUs?: string;
  commercialWaitlist?: string;
};

type UseJoinExpMenuItemsParams = {
  isJoinExp: boolean;
  baseMenuItems: MenuItem[];
  buttonLabels?: ButtonLabels;
  joinPageContactPath?: string;
  joinPageContactPage?: string;
  trackExpEvent: (event: string) => void;
};

/**
 * Custom hook to manage menu items for Join eXp pages with responsive button behavior.
 * 
 * Handles the logic for showing/hiding "Contact Us" button based on screen size:
 * - Small mobile (≤380px): "Contact Us" in menu
 * - Medium mobile (381-768px): "Contact Us" as button
 * - Desktop (>768px): "Contact Us" as button
 * 
 * Note: "I'm Ready" button has been replaced by the Floating Action Button (FAB)
 * 
 * @param params Configuration object
 * @returns Object containing menuItems and visibility flags for buttons
 */
export function useJoinExpMenuItems({
  isJoinExp,
  baseMenuItems,
  buttonLabels,
  joinPageContactPath,
  joinPageContactPage,
  trackExpEvent,
}: UseJoinExpMenuItemsParams) {
  const isMobile = useIsMobile();

  const getJoinExpContactUrl = () => {
    if (isJoinExp) {
      return joinPageContactPath;
    }
    return joinPageContactPage;
  };

  // Only show the header Contact Us button on non-mobile screens.
  const shouldShowContactUsButton = isJoinExp && !isMobile;

  // Insert Contact Us before Accessibility on join pages.
  const menuItems = useMemo(() => {
    if (!isJoinExp) return baseMenuItems;

    const contactUsItem: MenuItem = {
      id: 'contact-us',
      label: buttonLabels?.contactUs || 'Contact Us',
      link: getJoinExpContactUrl(),
      newTab: true,
      onClick: () => trackExpEvent(GTM_CLICK_CONTACT_US_BUTTON_JOIN_EXP),
    };

    const accessibilityIndex = baseMenuItems.findIndex(item => item.id === 'accessibility');
    return accessibilityIndex >= 0
      ? [
        ...baseMenuItems.slice(0, accessibilityIndex),
        contactUsItem,
        ...baseMenuItems.slice(accessibilityIndex),
      ]
      : [contactUsItem, ...baseMenuItems];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJoinExp, isMobile, baseMenuItems, buttonLabels, joinPageContactPath, joinPageContactPage]);

  return {
    menuItems,
    shouldShowContactUsButton,
    getJoinExpContactUrl,
  };
}
